import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FaUser, 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt, 
  FaBuilding,
  FaCalendarAlt,
  FaArrowLeft,
  FaEdit,
  FaSave,
  FaTimes
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';
import { useAuth } from '../../context/AuthContext';

// Styled Components
const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  .header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
    
    .back-button {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background: none;
      border: none;
      color: #6c757d;
      cursor: pointer;
      padding: 0.5rem;
      border-radius: 8px;
      transition: all 0.2s ease;
      text-decoration: none;
      
      &:hover {
        color: #F68B21;
        background: rgba(246, 139, 33, 0.1);
      }
    }
  }
  
  h1 {
    font-size: 2rem;
    color: #141340;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 1rem;
    
    .icon {
      color: #F68B21;
    }
  }
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(246, 139, 33, 0.3);
  }
`;

const ProfileContainer = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(20, 19, 64, 0.05);
`;

const ProfileHeader = styled.div`
  background: linear-gradient(135deg, #141340 0%, #2a2868 100%);
  padding: 2rem;
  color: white;
  position: relative;
  
  .avatar-section {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    
    .avatar {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 2rem;
      font-weight: 700;
      color: white;
      border: 4px solid rgba(255, 255, 255, 0.2);
    }
    
    .user-info {
      h2 {
        margin: 0 0 0.5rem 0;
        font-size: 1.5rem;
      }
      
      .role {
        opacity: 0.9;
        font-size: 1rem;
      }
      
      .status {
        display: inline-block;
        background: rgba(40, 167, 69, 0.2);
        color: #28a745;
        padding: 0.25rem 0.75rem;
        border-radius: 20px;
        font-size: 0.85rem;
        margin-top: 0.5rem;
      }
    }
  }
`;

const ProfileBody = styled.div`
  padding: 2rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
  
  h3 {
    color: #141340;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1.1rem;
    
    .icon {
      color: #F68B21;
    }
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const InfoItem = styled.div`
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #F68B21;
  
  .label {
    font-size: 0.85rem;
    color: #6c757d;
    margin-bottom: 0.25rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    .icon {
      color: #F68B21;
      font-size: 0.9rem;
    }
  }
  
  .value {
    font-weight: 600;
    color: #141340;
  }
`;

const ActivitySection = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  
  h4 {
    color: #141340;
    margin-bottom: 1rem;
  }
  
  .activity-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  
  .activity-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background: white;
    border-radius: 8px;
    border-left: 4px solid #F68B21;
    
    .activity-icon {
      color: #F68B21;
      font-size: 1.2rem;
    }
    
    .activity-content {
      flex: 1;
      
      .activity-title {
        font-weight: 600;
        color: #141340;
        margin-bottom: 0.25rem;
      }
      
      .activity-time {
        font-size: 0.85rem;
        color: #6c757d;
      }
    }
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    font-weight: 600;
    color: #141340;
    margin-bottom: 0.5rem;
  }
  
  input, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    font-size: 0.9rem;
    transition: all 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: #F68B21;
      box-shadow: 0 0 0 3px rgba(246, 139, 33, 0.1);
    }
  }
  
  textarea {
    resize: vertical;
    min-height: 100px;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e9ecef;
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(246, 139, 33, 0.3);
  }
`;

const CancelButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  color: #6c757d;
  border: 1px solid #e9ecef;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    color: #F68B21;
    border-color: #F68B21;
    background: rgba(246, 139, 33, 0.1);
  }
`;

const ManagerProfile: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Mock user profile data
  const [profileData, setProfileData] = useState({
    name: user?.name || 'Hamadi Simba',
    email: user?.email || 'hamadi.simba@pfrs.co.tz',
    phone: '+255 712 345 678',
    address: 'Dar es Salaam, Tanzania',
    department: 'Project Management',
    position: user?.role || 'Project Manager',
    bio: 'Experienced project manager with over 5 years of experience in infrastructure development and community projects.',
    joinDate: '2022-03-15'
  });
  
  const [editData, setEditData] = useState(profileData);
  
  // Mock recent activities
  const recentActivities = [
    { id: 1, title: 'Approved fund request for Water Supply Project', time: '2 hours ago' },
    { id: 2, title: 'Updated project progress for Road Construction', time: '1 day ago' },
    { id: 3, title: 'Reviewed team assignments for School Building', time: '2 days ago' },
    { id: 4, title: 'Submitted monthly report for March 2024', time: '1 week ago' },
  ];
  
  const handleEdit = () => {
    setEditData(profileData);
    setIsEditing(true);
  };
  
  const handleCancel = () => {
    setIsEditing(false);
    setEditData(profileData);
  };
  
  const handleSave = async () => {
    setSaving(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setProfileData(editData);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save profile:', error);
    } finally {
      setSaving(false);
    }
  };
  
  const handleInputChange = (field: string, value: string) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const getUserInitials = () => {
    if (!profileData.name) return 'U';
    const nameParts = profileData.name.split(' ');
    if (nameParts.length > 1) {
      return `${nameParts[0][0]}${nameParts[1][0]}`;
    }
    return nameParts[0][0];
  };
  
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return new globalThis.Date(dateString).toLocaleDateString('en-US', options);
  };
  
  return (
    <PageContainer>
      <PageHeader>
        <div className="header-left">
          <button className="back-button" onClick={() => navigate('/manager')}>
            <IconWrapper icon={FaArrowLeft} />
            Back to Dashboard
          </button>
          <h1>
            <IconWrapper icon={FaUser} className="icon" />
            Profile
          </h1>
        </div>
        {!isEditing && (
          <EditButton onClick={handleEdit}>
            <IconWrapper icon={FaEdit} />
            Edit Profile
          </EditButton>
        )}
      </PageHeader>
      
      <ProfileContainer>
        <ProfileHeader>
          <div className="avatar-section">
            <div className="avatar">{getUserInitials()}</div>
            <div className="user-info">
              <h2>{profileData.name}</h2>
              <div className="role">{profileData.position}</div>
              <div className="status">Active</div>
            </div>
          </div>
        </ProfileHeader>
        
        <ProfileBody>
          {!isEditing ? (
            <>
              <Section>
                <h3>
                  <IconWrapper icon={FaUser} className="icon" />
                  Personal Information
                </h3>
                <InfoGrid>
                  <InfoItem>
                    <div className="label">
                      <IconWrapper icon={FaEnvelope} className="icon" />
                      Email Address
                    </div>
                    <div className="value">{profileData.email}</div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">
                      <IconWrapper icon={FaPhone} className="icon" />
                      Phone Number
                    </div>
                    <div className="value">{profileData.phone}</div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">
                      <IconWrapper icon={FaMapMarkerAlt} className="icon" />
                      Address
                    </div>
                    <div className="value">{profileData.address}</div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">
                      <IconWrapper icon={FaBuilding} className="icon" />
                      Department
                    </div>
                    <div className="value">{profileData.department}</div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">
                      <IconWrapper icon={FaCalendarAlt} className="icon" />
                      Join Date
                    </div>
                    <div className="value">{formatDate(profileData.joinDate)}</div>
                  </InfoItem>
                </InfoGrid>
              </Section>
              
              <Section>
                <h3>Bio</h3>
                <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px', lineHeight: '1.6' }}>
                  {profileData.bio}
                </div>
              </Section>
              
              <Section>
                <ActivitySection>
                  <h4>Recent Activities</h4>
                  <div className="activity-list">
                    {recentActivities.map(activity => (
                      <div key={activity.id} className="activity-item">
                        <IconWrapper icon={FaUser} className="activity-icon" />
                        <div className="activity-content">
                          <div className="activity-title">{activity.title}</div>
                          <div className="activity-time">{activity.time}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ActivitySection>
              </Section>
            </>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
              <Section>
                <h3>
                  <IconWrapper icon={FaUser} className="icon" />
                  Personal Information
                </h3>
                <InfoGrid>
                  <FormGroup>
                    <label>Full Name</label>
                    <input
                      type="text"
                      value={editData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Email Address</label>
                    <input
                      type="email"
                      value={editData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      value={editData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Address</label>
                    <input
                      type="text"
                      value={editData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                    />
                  </FormGroup>
                </InfoGrid>
              </Section>
              
              <Section>
                <h3>Bio</h3>
                <FormGroup>
                  <label>About Me</label>
                  <textarea
                    value={editData.bio}
                    onChange={(e) => handleInputChange('bio', e.target.value)}
                    placeholder="Tell us about yourself..."
                  />
                </FormGroup>
              </Section>
              
              <FormActions>
                <CancelButton type="button" onClick={handleCancel}>
                  <IconWrapper icon={FaTimes} />
                  Cancel
                </CancelButton>
                <SaveButton type="submit" disabled={saving}>
                  <IconWrapper icon={FaSave} />
                  {saving ? 'Saving...' : 'Save Changes'}
                </SaveButton>
              </FormActions>
            </form>
          )}
        </ProfileBody>
      </ProfileContainer>
    </PageContainer>
  );
};

export default ManagerProfile; 